import { useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { Button } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { SyncReviewWizardModal } from './SyncReviewWizardModal';

interface PublicationSyncReviewControlProps {
  /** User-catalog publication ids to walk through, in this order. */
  ids: number[];
  /** Publication titles by id, for the step header and the outcome list. */
  titles?: Record<number, string>;
  /**
   * At least one refresh landed; data should be refetched. Fires once, as
   * the modal closes — never per step.
   */
  onDone?: () => void;
  /**
   * The user closed the modal after walking the whole queue — the safe
   * point to clear the table selection (clearing it earlier would unmount
   * this control and the open wizard with it).
   */
  onClosed?: () => void;
  /** The modal opened or closed; keep this control mounted while open. */
  onOpenChange?: (open: boolean) => void;
}

export const PublicationSyncReviewControl = ({
  ids,
  titles,
  onDone,
  onClosed,
  onOpenChange,
}: PublicationSyncReviewControlProps) => {
  const t = useTranslate();
  // The run acts on what was selected when it started: a refetch elsewhere
  // on the page can drop rows out of the view, and a live `ids` would
  // reshuffle the queue mid-walk.
  const [run, setRun] = useState<{
    ids: number[];
    titles?: Record<number, string>;
  } | null>(null);

  const start = () => {
    setRun({ ids, titles });
    onOpenChange?.(true);
  };

  return (
    <>
      <Button
        icon={<SyncOutlined />}
        disabled={ids.length === 0}
        onClick={start}
      >
        {t('user_catalog.publications.sync_review.button')}
      </Button>

      {run && (
        <SyncReviewWizardModal
          queue={run.ids}
          titles={run.titles}
          onDone={onDone}
          onClose={(walked) => {
            setRun(null);
            onOpenChange?.(false);
            if (walked) onClosed?.();
          }}
        />
      )}
    </>
  );
};
